import { ThunkDispatch } from "core-integration/src/store";
import {
  getElementTypes,
  getElementTypesById,
} from "core-integration/src/store/element-types/reducers";
import { updateElementControls } from "core-integration/src/store/elements/actions";
import {
  getElementControls,
  getElementsFromControls,
} from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
} from "core-integration/src/store/elements/thunks";
import {
  IElement,
  IElementControls,
} from "core-integration/src/store/elements/types";
import {
  getProposedElementsById,
  getTypesNeeded,
} from "core-integration/src/store/squad/reducers";
import {
  proposeElement,
  removeElement,
} from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import groupBy from "lodash/groupBy";
import range from "lodash/range";
import { ellipsis } from "polished";
import * as React from "react";
import { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import Alert from "../../Alert";
import Button from "../../Button";
import Dialog, { DialogButtonItem } from "../../Dialog";
import DialogHeading from "../../DialogHeading";
import Paginator from "../../element-controls/Paginator";
import ElementDialogButton from "../../ElementDialogButton";
import ElementShirt from "../../ElementShirt";
import { ElementRow, ElementTable } from "../../ElementTable";
import { FieldWrap, SearchField, SelectField } from "../../FieldRenderers";
import Media from "../../Media";
import Panel from "../../Panel";
import Tooltip, { TooltipLabel } from "../../Tooltip";
import ElementFilter from "./ElementFilter";
import { ToggleButtonGroup } from "./ToggleButtonGroup";

const StyledFieldCols = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
    flex-wrap: wrap;
  }
`;

const StyledFieldCol = styled.div<{ $order?: number }>`
  padding-right: ${({ theme }) => theme.space[2]};
  padding-left: ${({ theme }) => theme.space[2]};
  flex: 1 1 50%;
  order: ${({ $order }) => $order ?? 0};
`;

const TableWrap = styled.div`
  th {
    &:first-of-type {
      border-top-left-radius: ${({ theme }) => theme.radii[2]};
    }

    &:last-of-type {
      border-top-right-radius: ${({ theme }) => theme.radii[2]};
    }
  }
`;

const StatsElement = styled.th`
  width: 28%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 38%;
  }
`;

const StatsCost = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsSel = styled.th`
  width: 10%;
  white-space: normal;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsForm = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsPts = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsStatus = styled.th`
  width: 7%;
`;

const Name = styled.div`
  ${ellipsis()};
  font-weight: bold;
  &:hover {
    text-decoration: underline;
  }
`;

const Team = styled.div`
  margin-right: ${({ theme }) => theme.space[2]};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: normal;
`;

const ElementNameButton = styled.button`
  ${ellipsis()};
  width: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  color: inherit;
  font-family: ${({ theme }) => theme.fonts.base};
  font-weight: bold;
  text-align: left;
  cursor: pointer;
`;

const ElementsShown = styled.p`
  margin: ${({ theme }) => theme.space[2]} 0;
  text-align: center;

  strong {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const PlayerActions = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 50%;
    margin: 0 auto;
  }
`;

interface IElementListProps {
  renderWidget: (element: IElement) => void;
  widgetVisible: boolean;
  hideWidget: () => void;
}

type Props = IElementListProps & WithTranslation;

const ElementList = ({ t, renderWidget, widgetVisible, hideWidget }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [elementForMenu, setElementForMenu] = useState<IElement | null>(null);
  const [page, setPage] = useState<number>(1);

  const [positionFilter, setPositionFilter] = useState<number>(1);

  const controls = useSelector(getElementControls);
  const currencyDivisor = 10;
  const elementTypes = useSelector(getElementTypes);
  const elementTypesById = useSelector(getElementTypesById);
  const rawElements = useSelector(getElementsFromControls);

  const elements = {
    ...rawElements,
    data: rawElements.data.filter((e) => e.element_type === positionFilter),
  };

  const proposedElementsById = useSelector(getProposedElementsById);
  const teamsById = useSelector(getTeamsById);
  const typesNeeded = useSelector(getTypesNeeded);

  const handleAddElement = (elementId: number) => {
    dispatch(proposeElement(elementId));
    handleHideMenuForElement();
  };

  const handleRemoveElement = (position: number) => {
    dispatch(removeElement(position));
    handleHideMenuForElement();
  };

  const updateControls = (controls: IElementControls) =>
    dispatch(updateElementControls(controls));

  const updateControlsAndMaxCost = (controls: IElementControls) =>
    dispatch(updateElementControlsAndMaxCost(controls));

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    updateControlsAndMaxCost({
      ...controls,
      filter: e.target.value,
    });
    hideWidget();
  };

  const handleTogglePositionChange = (value: string) => {
    setPage(1);
    setPositionFilter(Number(value));
    hideWidget();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    updateControls({
      ...controls,
      search: e.target.value,
    });
    hideWidget();
  };

  const handleMaxCostChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    updateControls({
      ...controls,
      maxCost: Number(e.target.value),
    });
    hideWidget();
  };

  const handleHideMenuForElement = () => {
    setElementForMenu(null);
  };

  const showDialog = (element: IElement) => {
    dispatch(showElementSummary(element.id));
    handleHideMenuForElement();
  };

  useEffect(() => {
    updateControlsAndMaxCost({
      ...controls,
      filter: "all",
      sort: "total_points",
      search: "",
      getUnavailable: false,
    });
  }, []);

  const paginateAndGroup = (elements: IElement[], pageSize: number) => {
    const start = (page - 1) * pageSize;
    return {
      data: groupBy(elements.slice(start, start + pageSize), "element_type"),
      totalPages: Math.ceil(elements.length / pageSize),
    };
  };

  const renderMenu = () => {
    const element = elementForMenu;

    if (!element) {
      return null;
    }

    const proposedElement = proposedElementsById[element.id];
    const roomInSquad = typesNeeded[element.element_type];

    let alert = null;
    if (!roomInSquad) {
      alert = (
        <Alert type="error" noRadius>
          {t(
            "elementList.errors.maxElementType",
            "You already have the maximum number of {{ position }}",
            {
              position: elementTypesById[element.element_type].plural_name,
            },
          )}
        </Alert>
      );
    } else if (proposedElement) {
      alert = (
        <Alert type="warning" noRadius>
          <Trans
            i18nKey="elementList.errors.alreadySelected"
            values={{ playerName: element.web_name }}
          >
            <strong>{element.web_name}</strong> is already in your squad.
          </Trans>
        </Alert>
      );
    }

    return (
      <Dialog closeDialog={handleHideMenuForElement}>
        <Dialog.Header closeDialog={handleHideMenuForElement}>
          <Box py={2} px={3}>
            <DialogHeading>
              {`${element.first_name} ${element.second_name}`}
            </DialogHeading>
          </Box>
        </Dialog.Header>
        {alert && alert}

        <Dialog.Body>
          <PlayerActions>
            <DialogButtonItem>
              {proposedElement ? (
                <Button
                  onClick={() => handleRemoveElement(proposedElement.position)}
                  width={1}
                >
                  {t("elementList.removePlayer", "Remove player")}
                </Button>
              ) : (
                <Button
                  onClick={() => handleAddElement(element.id)}
                  disabled={Boolean(!roomInSquad)}
                  width={1}
                >
                  {t("elementList.addPlayer", "Add player")}
                </Button>
              )}
            </DialogButtonItem>
            <DialogButtonItem>
              <Button onClick={() => showDialog(element)} width={1}>
                {t("viewInformation", "View information")}
              </Button>
            </DialogButtonItem>
          </PlayerActions>
        </Dialog.Body>
      </Dialog>
    );
  };

  const { data, totalPages } = paginateAndGroup(elements.data, 10);
  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <StyledFieldCols>
          <StyledFieldCol $order={1}>
            <ToggleButtonGroup
              onChange={handleTogglePositionChange}
              value={positionFilter.toString()}
              label={t("xStats.toggleButtonGroup.label", "Filter by position")}
              options={[
                { label: "GKP", value: "1" },
                { label: "DEF", value: "2" },
                { label: "MID", value: "3" },
                { label: "FWD", value: "4" },
              ]}
            />
          </StyledFieldCol>
          <StyledFieldCol $order={2}>
            <ElementFilter handleFilterChange={handleFilterChange} />
          </StyledFieldCol>

          <StyledFieldCol $order={4}>
            <FieldWrap>
              <SelectField
                id="maxCost"
                name="maxCost"
                label={t("elementList.maxCost.label", "Max cost")}
                value={controls.maxCost}
                onChange={handleMaxCostChange}
                required={false}
              >
                {range(elements.maxCost, elements.minCost - 1, -5).map(
                  (cost) => (
                    <option
                      key={cost}
                      value={cost}
                      aria-selected={controls.maxCost === Number(cost)}
                    >
                      {integerToMoney(cost, 10) + " mkr"}
                    </option>
                  ),
                )}
              </SelectField>
            </FieldWrap>
          </StyledFieldCol>
          <StyledFieldCol $order={3}>
            <FieldWrap>
              <SearchField
                id="search"
                name="search"
                label={t("elementList.search", "Search Player List")}
                onChange={handleSearchChange}
                value={controls.search}
                required={false}
              />
            </FieldWrap>
          </StyledFieldCol>
        </StyledFieldCols>
      </form>

      {!widgetVisible && (
        <>
          <ElementsShown>
            <Trans
              i18nKey="elementList.playersShown"
              values={{ length: elements.data.length }}
              defaults={"<0>{{length}}</0> players shown"}
              components={[<strong />]}
            />
          </ElementsShown>
          <TableWrap>
            {elementTypes.map(
              (et) =>
                data[et.id] && (
                  <Panel key={et.id}>
                    <ElementTable key={et.id} elementType={et.id}>
                      <thead>
                        <tr>
                          <StatsStatus>&nbsp;</StatsStatus>
                          <StatsElement>
                            {t("statistics.player", "Player")}
                          </StatsElement>
                          <StatsCost>Mkr</StatsCost>
                          <StatsSel>
                            <Tooltip
                              content={t(
                                "statistics.selectedByLong",
                                "Selected by %",
                              )}
                            >
                              <TooltipLabel>
                                {t("statistics.selectedByShort", "Sel.")}
                              </TooltipLabel>
                            </Tooltip>
                          </StatsSel>
                          <StatsForm>{t("statistics.form", "Form")}</StatsForm>
                          <StatsPts>
                            <Tooltip
                              content={t(
                                "statistics.totalPoints",
                                "Total points",
                              )}
                            >
                              <TooltipLabel>
                                {t("statistics.totalPointsShort", "TP.")}
                              </TooltipLabel>
                            </Tooltip>
                          </StatsPts>
                        </tr>
                      </thead>
                      <tbody>
                        {data[et.id].map((e) => (
                          <ElementRow key={e.id}>
                            <td>
                              <ElementDialogButton
                                elementId={e.id}
                                variant="list"
                              />
                            </td>
                            <td>
                              <ElementNameButton
                                onClick={() => renderWidget(e)}
                              >
                                <Media
                                  img={
                                    <ElementShirt
                                      elementId={e.id}
                                      sizes="24px"
                                    />
                                  }
                                  centred={true}
                                >
                                  <Name>{e.web_name}</Name>
                                  <Team>{teamsById[e.team].short_name}</Team>
                                </Media>
                              </ElementNameButton>
                            </td>
                            <td>
                              {integerToMoney(e.now_cost, currencyDivisor)}
                            </td>
                            <td>{e.selected_by_percent}%</td>
                            <td>{e.form}</td>
                            <td>{e.total_points}</td>
                          </ElementRow>
                        ))}
                      </tbody>
                    </ElementTable>
                  </Panel>
                ),
            )}
          </TableWrap>
          <Paginator totalPages={totalPages} page={page} setPage={setPage} />
        </>
      )}

      {renderMenu()}
    </>
  );
};

export default withTranslation()(ElementList);
